// Storage format 2: artwork and fonts live once in a content-addressed store
// and the design holds `ref:<sha256>` strings. Format 1 embedded data URLs,
// packed into doc.assets (art0…) by the editor. Both remain readable.
const { REF } = require("./validate");

const DATA = /^data:([a-z]+\/[a-z0-9.+-]+);base64,([A-Za-z0-9+/=]+)$/i;
const isRef = (v) => typeof v === "string" && REF.test(v);
const isData = (v) => typeof v === "string" && DATA.test(v);

// Replace every embedded image/font with a reference. `store(dataUrl)` must
// persist the bytes and return "ref:<sha256>" (synchronously or via a map
// prepared beforehand). Also unpacks format-1 asset tables.
function externalize(doc, store) {
  const assets = doc.assets || {};
  const pages = doc.pages.map((page) => ({ ...page, elements: page.elements.map((node) => {
    if (node.type !== "image") return node;
    const src = node.src || (node.assetId ? assets[node.assetId] : undefined);
    const { assetId, ...rest } = node;
    void assetId;
    return { ...rest, src: isData(src) ? store(src) : src };
  }) }));
  const fonts = Array.isArray(doc.fonts) ? doc.fonts.map((f) => (isData(f.src) ? { ...f, src: store(f.src) } : f)) : doc.fonts;
  const { assets: unused, ...rest } = doc;
  void unused;
  const out = { ...rest, format: 2, pages };
  if (fonts !== undefined) out.fonts = fonts;
  return out;
}

// Inverse: resolve references to data URLs (for format-1 clients, exports and
// recovery packages). `load(ref)` returns a data URL.
function inflate(doc, load) {
  const pages = doc.pages.map((page) => ({ ...page, elements: page.elements.map((node) =>
    node.type === "image" && isRef(node.src) ? { ...node, src: load(node.src) } : node) }));
  const fonts = Array.isArray(doc.fonts) ? doc.fonts.map((f) => (isRef(f.src) ? { ...f, src: load(f.src) } : f)) : doc.fonts;
  const { format, ...rest } = doc;
  void format;
  const out = { ...rest, pages };
  if (fonts !== undefined) out.fonts = fonts;
  return out;
}

// Every reference a document uses (for retention and recovery packages).
function references(doc) {
  const refs = new Set();
  doc.pages.forEach((p) => p.elements.forEach((n) => { if (isRef(n.src)) refs.add(n.src); }));
  (doc.fonts || []).forEach((f) => { if (isRef(f.src)) refs.add(f.src); });
  Object.values(doc.assets || {}).forEach((v) => { if (isRef(v)) refs.add(v); });
  return [...refs];
}

// Data URLs embedded anywhere inside operation values (new artwork, pasted
// nodes, replaced fonts), so the server can move them into the store.
function mapDataUrls(value, fn) {
  if (typeof value === "string") return isData(value) ? fn(value) : value;
  if (Array.isArray(value)) return value.map((v) => mapDataUrls(v, fn));
  if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, mapDataUrls(v, fn)]));
  return value;
}

function parseData(dataUrl) {
  const m = DATA.exec(dataUrl);
  return m ? { mime: m[1].toLowerCase(), base64: m[2] } : null;
}

module.exports = { externalize, inflate, references, mapDataUrls, parseData, isRef, isData };
